import React, { useState, useEffect, useCallback, useRef } from 'react';
import ProtectedRoute from '../../routes/protected';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import { Chip } from 'primereact/chip';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import UsePerms from '../../context/userAccess';
import useCompanyData from '../../context/company';
import surveyService from '../../services/data/survey.service';
import SurveyPageHeader from './SurveyPageHeader';

const SURVEY_STATUS_CHIP = {
    SC: { label: 'Rascunho', className: 'bg-secondary text-white border-0' },
    AC: { label: 'Ativa', className: 'bg-success text-white border-0' },
    CL: { label: 'Encerrada', className: 'bg-dark text-white border-0' },
};

const FREQ_HINT = {
    W: 'Semanal — envios a cada 7 dias',
    B: 'Quinzenal — envios a cada 14 dias',
    M: 'Mensal — envios a cada 30 dias',
};

function isAutoOnlySurvey(survey) {
    return !survey?.frequency;
}

function SurveyStatusChip({ status }) {
    const cfg = SURVEY_STATUS_CHIP[status] || { label: status, className: 'bg-secondary text-white border-0' };
    return <Chip label={cfg.label} className={cfg.className} />;
}

function errDetail(err) {
    const d = err.response?.data?.detail;
    if (Array.isArray(d)) return d.map((x) => (typeof x === 'string' ? x : x?.message || JSON.stringify(x))).join(' ');
    if (typeof d === 'string') return d;
    return err.message || 'Erro';
}

export default function SurveyList() {
    const navigate = useNavigate();
    const toast = React.useRef(null);
    const VerifyPerms = UsePerms();
    const { rootUser } = useCompanyData();
    const companyId = localStorage.getItem('companyId');
    const canCreateSurvey = VerifyPerms(['add_survey']) || rootUser;
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page] = useState(1);

    const [showActivateDialog, setShowActivateDialog] = useState(false);
    const [activatingSurvey, setActivatingSurvey] = useState(null);
    const [actStart, setActStart] = useState(null);
    const [actEnd, setActEnd] = useState(null);
    const [schedulePreview, setSchedulePreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [activateSubmitting, setActivateSubmitting] = useState(false);
    const previewTimer = useRef(null);

    useEffect(() => {
        loadSurveys();
    }, [page]);

    function loadSurveys() {
        if (!companyId) return;
        setLoading(true);
        surveyService.listSurveys(page, { company_id: companyId })
            .then(r => {
                setSurveys(r.results || r);
            })
            .catch(err => {
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Erro', detail: errDetail(err) });
            })
            .finally(() => setLoading(false));
    }

    const runSchedulePreview = useCallback(() => {
        if (!activatingSurvey || !companyId || !actStart || !actEnd) return;
        if (isAutoOnlySurvey(activatingSurvey)) return;
        if (actEnd <= actStart) {
            setSchedulePreview(null);
            return;
        }
        setPreviewLoading(true);
        surveyService.previewActivateSchedule({
            id: activatingSurvey.id,
            company_id: companyId,
            start_date: actStart.toISOString(),
            end_date: actEnd.toISOString(),
        })
            .then(setSchedulePreview)
            .catch((err) => {
                setSchedulePreview(null);
                if (toast.current) toast.current.show({ severity: 'warn', summary: 'Prévia', detail: errDetail(err) });
            })
            .finally(() => setPreviewLoading(false));
    }, [activatingSurvey, companyId, actStart, actEnd]);

    useEffect(() => {
        if (!showActivateDialog || !activatingSurvey) return;
        if (previewTimer.current) clearTimeout(previewTimer.current);
        previewTimer.current = setTimeout(() => runSchedulePreview(), 400);
        return () => clearTimeout(previewTimer.current);
    }, [showActivateDialog, activatingSurvey, actStart, actEnd, runSchedulePreview]);

    function openActivateDialog(survey) {
        const start = new Date();
        start.setSeconds(0, 0);
        start.setHours(9, 0, 0, 0);
        if (start.getTime() < Date.now()) {
            start.setDate(start.getDate() + 1);
        }
        const end = new Date(start);
        const add = { W: 7, B: 14, M: 30 }[survey.frequency || 'M'] || 30;
        end.setDate(end.getDate() + add);
        end.setHours(18, 0, 0, 0);
        setActivatingSurvey(survey);
        setActStart(start);
        setActEnd(end);
        setSchedulePreview(null);
        setShowActivateDialog(true);
    }

    function submitActivate() {
        if (!activatingSurvey || !companyId) return;
        if (!isAutoOnlySurvey(activatingSurvey) && (!actStart || !actEnd)) return;
        setActivateSubmitting(true);
        const payload = {
            id: activatingSurvey.id,
            company_id: companyId,
        };
        if (!isAutoOnlySurvey(activatingSurvey)) {
            payload.start_date = actStart.toISOString();
            payload.end_date = actEnd.toISOString();
        }
        surveyService.activateSurvey(payload)
            .then((data) => {
                const ns = data.notification_schedule;
                let detail = 'Pesquisa ativada.';
                if (ns?.mode === 'auto_only') {
                    detail = 'Pesquisa ativada. Configure os gatilhos automáticos no dashboard da pesquisa.';
                } else if (ns) {
                    detail += ` ${ns.future_slots} envio(s) agendado(s) (intervalo ${ns.interval_days} dias).`;
                }
                if (toast.current) toast.current.show({ severity: 'success', summary: 'Sucesso', detail });
                setShowActivateDialog(false);
                setActivatingSurvey(null);
                loadSurveys();
            })
            .catch((err) => {
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Erro', detail: errDetail(err) });
            })
            .finally(() => setActivateSubmitting(false));
    }

    function handleClose(survey) {
        surveyService.closeSurvey({ id: survey.id })
            .then(() => {
                if (toast.current) toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Pesquisa encerrada' });
                loadSurveys();
            })
            .catch(err => {
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Erro', detail: errDetail(err) });
            });
    }

    const periodDateTimeOpts = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    };

    function formatPeriod(s) {
        if (isAutoOnlySurvey(s) && s.status === 'AC') {
            return 'Disparo automático';
        }
        const fmt = (iso) => new Date(iso).toLocaleString('pt-BR', periodDateTimeOpts);
        if (s.start_date && s.end_date) {
            return `${fmt(s.start_date)} — ${fmt(s.end_date)}`;
        }
        if (s.start_date) return fmt(s.start_date);
        return '—';
    }

    const activatingAutoOnly = activatingSurvey && isAutoOnlySurvey(activatingSurvey);

    const activateFooter = (
        <div>
            <button type="button" className="btn btn-outline-secondary btn-rounded me-2" onClick={() => setShowActivateDialog(false)} disabled={activateSubmitting}>
                Cancelar
            </button>
            <button
                type="button"
                className="btn btn-success btn-rounded"
                disabled={
                    activateSubmitting
                    || (!activatingAutoOnly && (!schedulePreview || schedulePreview.future_slots < 1))
                }
                onClick={submitActivate}
            >
                {activateSubmitting ? 'Ativando...' : 'Confirmar ativação'}
            </button>
        </div>
    );

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid">
                <SurveyPageHeader title="Gestão de Pesquisas" />
                <div className="my-4 mx-5 py-3 rounded-top" style={{ minHeight: '500px' }}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="text-default mb-0">
                        <FontAwesomeIcon icon={solid('clipboard-list')} className="me-2 text-green" />
                        Pesquisas por Pulso
                    </h5>
                    {canCreateSurvey && (
                        <button className="btn btn-success btn-rounded btn-sm px-3" onClick={() => navigate('/survey/create/')}>
                            <FontAwesomeIcon icon={solid('plus')} className="me-1" /> Nova Pesquisa
                        </button>
                    )}
                </div>

                <Dialog
                    header={activatingAutoOnly ? 'Ativar pesquisa — disparo automático' : 'Ativar pesquisa — período e envios'}
                    visible={showActivateDialog}
                    style={{ width: '560px' }}
                    onHide={() => !activateSubmitting && setShowActivateDialog(false)}
                    footer={activateFooter}
                >
                    {activatingSurvey && (
                        <div className="small text-muted mb-3">
                            <strong className="text-default">{activatingSurvey.name}</strong>
                            <br />
                            {activatingAutoOnly
                                ? 'Sem campanha manual. Após ativar, configure os gatilhos no dashboard da pesquisa.'
                                : (FREQ_HINT[activatingSurvey.frequency] || FREQ_HINT.M)}
                        </div>
                    )}
                    {activatingAutoOnly ? (
                        <div className="p-3 bg-light rounded small text-muted">
                            Os colaboradores receberão a pesquisa conforme os gatilhos que você definir
                            (tempo de casa, cargo, escopo, etc.). Nenhum e-mail em massa será agendado nesta ativação.
                        </div>
                    ) : (
                    <>
                    <div className="row g-3">
                        <div className="col-12 col-md-6">
                            <label className="form-label small fw-bold">Início da pesquisa</label>
                            <Calendar
                                value={actStart}
                                onChange={(e) => setActStart(e.value)}
                                showTime
                                hourFormat="24"
                                dateFormat="dd/mm/yy"
                                className="w-100"
                                inputClassName="w-100"
                            />
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label small fw-bold">Término da pesquisa</label>
                            <Calendar
                                value={actEnd}
                                onChange={(e) => setActEnd(e.value)}
                                showTime
                                hourFormat="24"
                                dateFormat="dd/mm/yy"
                                className="w-100"
                                inputClassName="w-100"
                            />
                        </div>
                    </div>
                    <div className="mt-3 p-3 bg-light rounded small">
                        {previewLoading && <span className="text-muted">Calculando envios…</span>}
                        {!previewLoading && schedulePreview && (
                            <>
                                <div>
                                    <strong>Intervalo entre envios:</strong> {schedulePreview.interval_days} dias (
                                    {schedulePreview.frequency_display})
                                </div>
                                <div>
                                    <strong>Total de disparos no período:</strong> {schedulePreview.total_slots}
                                </div>
                                <div>
                                    <strong>Envios ainda a agendar (futuros):</strong>{' '}
                                    <span className={schedulePreview.future_slots < 1 ? 'text-danger' : 'text-success'}>
                                        {schedulePreview.future_slots}
                                    </span>
                                </div>
                                {schedulePreview.future_slots < 1 && (
                                    <div className="text-danger mt-2">Ajuste as datas para haver pelo menos um envio futuro.</div>
                                )}
                            </>
                        )}
                        {!previewLoading && !schedulePreview && actStart && actEnd && actEnd > actStart && (
                            <span className="text-muted">Não foi possível calcular a prévia.</span>
                        )}
                    </div>
                    </>
                    )}
                </Dialog>

                <div className="bg-white rounded border-clean p-3">
                    {loading ? (
                        <div className="text-center py-5 text-muted">Carregando...</div>
                    ) : surveys.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            Nenhuma pesquisa cadastrada. <br /> {canCreateSurvey && <button onClick={() => navigate('/survey/create/')} className="btn btn-success btn-rounded btn-sm mt-2 px-4">Criar primeira pesquisa</button>}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead>
                                    <tr>
                                        <th className="align-middle">Nome</th>
                                        <th className="text-center align-middle">Template</th>
                                        <th className="text-center align-middle">Status</th>
                                        <th className="text-center align-middle">Respostas</th>
                                        <th className="text-center align-middle">Período</th>
                                        <th className="align-middle">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {surveys.map((s) => (
                                        <tr key={s.id} className="align-middle">
                                            <td>
                                                <a href={`/survey/dashboard/${s.id}/`} className="text-green fw-bold text-decoration-none">{s.name}</a>
                                                <br />
                                                <small className="text-muted">
                                                    {s.created_by_name ? (
                                                        <>Criado por <span className="text-default">{s.created_by_name}</span></>
                                                    ) : (
                                                        'Criador não informado'
                                                    )}
                                                </small>
                                            </td>
                                            <td className="text-center">{s.template_name || s.template?.name}</td>
                                            <td className="text-center">
                                                <SurveyStatusChip status={s.status} />
                                                {s.auto_dispatch_enabled && s.active_triggers_count > 0 && (
                                                    <div className="mt-1">
                                                        <Chip label="Auto" className="bg-info text-white border-0" style={{ fontSize: '0.7rem' }} />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-center">{s.response_count ?? 0}</td>
                                            <td className="text-center small">{formatPeriod(s)}</td>
                                            <td>
                                                {s.status === 'SC' && (
                                                    <button className="btn btn-sm btn-success btn-rounded px-3 me-1" onClick={() => openActivateDialog(s)}>
                                                        Ativar
                                                    </button>
                                                )}
                                                {s.status === 'AC' && (
                                                    <button className="btn btn-sm btn-warning btn-rounded px-3" onClick={() => handleClose(s)}>
                                                        Encerrar
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                </div>
            </div>
        </ProtectedRoute>
    );
}
